import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { articlesForIcp } from './aeoArticlesData';
import { aeoCoverForSlug } from './aeoCovers';

// Above this count the row no longer fits on one line, so it becomes a carousel rather than
// wrapping into a ragged second row.
const CAROUSEL_THRESHOLD = 3;

/**
 * Hub-page "Resources" block — links down to every AEO article in this ICP's cluster
 * (hub-and-spoke internal linking). Rendered below main hub content, above the footer.
 * Cards reuse the resources-page .lp-res-card design (cover, category, title, excerpt)
 * so the hub sections match the /resources grid exactly.
 *
 * Up to three articles render as a plain grid; beyond that they scroll horizontally with
 * prev/next controls, three visible at desktop width.
 */
const AeoResourcesSection = ({ icpSlug, heading = 'Resources' }) => {
    const articles = articlesForIcp(icpSlug);
    const trackRef = useRef(null);
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(false);

    const isCarousel = articles.length > CAROUSEL_THRESHOLD;

    const syncArrows = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        // 2px tolerance — fractional scroll widths otherwise leave the end arrow permanently enabled.
        setAtStart(el.scrollLeft <= 2);
        setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
    }, []);

    useEffect(() => {
        if (!isCarousel) return;
        const el = trackRef.current;
        if (!el) return;
        syncArrows();
        el.addEventListener('scroll', syncArrows, { passive: true });
        window.addEventListener('resize', syncArrows);
        return () => {
            el.removeEventListener('scroll', syncArrows);
            window.removeEventListener('resize', syncArrows);
        };
    }, [isCarousel, syncArrows, articles.length]);

    const scrollByPage = (direction) => {
        const el = trackRef.current;
        if (!el) return;
        const card = el.querySelector('.lp-aeo-res-card');
        // Advance one card at a time so partially-visible cards are never skipped.
        const step = card ? card.getBoundingClientRect().width + 22 : el.clientWidth;
        el.scrollBy({ left: direction * step, behavior: 'smooth' });
    };

    if (!articles.length) return null;

    const cards = articles.map(a => {
        const href = `/${a['icpSlug']}/${a['slug']}`;
        return (
            <div className="lp-res-card lp-aeo-res-card" key={a['slug']}>
                <Link to={href} className="img cp" style={{ backgroundImage: `url(${aeoCoverForSlug(a['slug'], a['image'])})`, display: 'block' }} aria-label={a['title']} />
                <div className="body">
                    <div className="cats">{a['category']}</div>
                    <Link to={href} style={{ textDecoration: 'none', color: 'inherit' }}><h3 className="cp">{a['title']}</h3></Link>
                    <p>{a['metaDescription'].replace(/\s*Last updated:.*$/, '')}</p>
                    <Link to={href}>Read article →</Link>
                </div>
            </div>
        );
    });

    return (
        <section className="lp-aeo-resources">
            <div className="lp-aeo-inner">
                {/* Controls sit beside the heading — the same placement the short-films carousel uses —
                    rather than floating over the cards. */}
                <div className="lp-aeo-resources-head">
                    <h2 className="lp-h2">{heading}</h2>
                    {isCarousel && (
                        <div className="lp-arrows">
                            <button
                                type="button"
                                className="lp-arrow"
                                onClick={() => scrollByPage(-1)}
                                disabled={atStart}
                                aria-label="Previous resources"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                            </button>
                            <button
                                type="button"
                                className="lp-arrow"
                                onClick={() => scrollByPage(1)}
                                disabled={atEnd}
                                aria-label="Next resources"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                            </button>
                        </div>
                    )}
                </div>

                {isCarousel ? (
                    <div className="lp-aeo-carousel">
                        <div className="track" ref={trackRef}>{cards}</div>
                    </div>
                ) : (
                    <div className="lp-aeo-resources-grid">{cards}</div>
                )}
            </div>
        </section>
    );
};

export default AeoResourcesSection;
